/**
 * Uma seção das Configurações (settingsModel.ts): o título, o subtítulo e o selo de onde ela grava,
 * os blocos do topo, os cards com as linhas e, nas de preferências, "Restaurar padrões" no fim.
 * `highlight` rola até a linha achada pela busca (ou pedida por um link) e a destaca por um tempo.
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useT } from '../i18n/index.js';
import { useApp } from '../store.js';
import { Button } from '../ui/controls.js';
import { Icon } from '../ui/icons.js';
import { PageHeader } from '../ui/PageHeader.js';
import { toast } from '../ui/Toaster.js';
import { AvTest } from './AvSection.js';
import { ChatPreview } from './ChatPreview.js';
import { DiceEffects, DiceLook } from './DiceSection.js';
import { No3DBanner } from './No3DBanner.js';
import { NoticesBanner } from './NoticesBanner.js';
import { PlanSection } from './PlanSection.js';
import { ProfileSection } from './ProfileSection.js';
import { SettingRow, type OpenSection } from './SettingRow.js';
import { sectionById, sectionDefaults, type BlockId, type Card, type SectionId } from './settingsModel.js';

/** quanto tempo a linha achada fica destacada */
const FLASH_MS = 1800;

function Block({ id }: { id: BlockId }) {
  switch (id) {
    case 'profile':
      return <ProfileSection />;
    case 'plan':
      return <PlanSection />;
    case 'chatPreview':
      return <ChatPreview />;
    case 'noticesBanner':
      return <NoticesBanner />;
    case 'no3dBanner':
      return <No3DBanner />;
    case 'diceLook':
      return <DiceLook />;
    case 'diceEffects':
      return <DiceEffects />;
    case 'avTest':
      return <AvTest />;
  }
}

function CardView({ card, flash, onOpen }: { card: Card; flash: string | null; onOpen: OpenSection }) {
  const t = useT();
  const titleId = `pref-card-${card.id}`;
  return (
    <section className={`rd-prefs__card${card.tone ? ` rd-prefs__card--${card.tone}` : ''}`} aria-labelledby={titleId}>
      <header className="rd-prefs__cardHead">
        <h3 id={titleId}>{t(card.title)}</h3>
        {card.desc ? <p className="rd-prefs__desc">{t(card.desc)}</p> : null}
      </header>
      {card.rows.map((row) =>
        row.kind === 'block' ? (
          <div key={row.id} className={`rd-prefs__blockRow${flash === row.id ? ' is-flash' : ''}`} data-pref-row={row.id}>
            <Block id={row.block} />
          </div>
        ) : (
          <SettingRow key={row.id} row={row} flash={flash === row.id} onOpen={onOpen} />
        ),
      )}
    </section>
  );
}

/** "Restaurar padrões de <seção>", com a confirmação no lugar. */
function ResetSection({ id, title }: { id: SectionId; title: string }) {
  const t = useT();
  const update = useApp((s) => s.updateSettings);
  const [confirming, setConfirming] = useState(false);
  if (!confirming) {
    return (
      <div className="rd-prefs__reset">
        <Button variant="link" className="rd-prefs__link" onClick={() => setConfirming(true)}>
          {t('prefs.reset', { section: title })}
        </Button>
      </div>
    );
  }
  return (
    <div className="rd-prefs__reset rd-prefs__reset--confirm" role="group" aria-label={t('prefs.reset', { section: title })}>
      <span role="alert">{t('prefs.reset.confirm', { section: title })}</span>
      <Button variant="secondary" onClick={() => setConfirming(false)}>
        {t('prefs.back')}
      </Button>
      <Button
        variant="action"
        autoFocus
        onClick={() => {
          update(sectionDefaults(id));
          setConfirming(false);
          toast(t('prefs.reset.done', { section: title }), 'success');
        }}
      >
        {t('prefs.reset.yes')}
      </Button>
    </div>
  );
}

export function SettingsSection({ id, highlight, onOpen, before }: { id: SectionId; highlight: { row: string; nonce: number } | null; onOpen: OpenSection; before?: ReactNode }) {
  const t = useT();
  const section = sectionById(id);
  const root = useRef<HTMLElement>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const title = t(section.title);
  const account = section.scope === 'account';

  // rola até a linha pedida e a destaca; o perfil e o plano carregam depois, então tenta mais um pouco
  useEffect(() => {
    if (!highlight) return;
    let tries = 0;
    let timer = 0;
    let lit: HTMLElement | null = null;
    const find = () => {
      const el = root.current?.querySelector<HTMLElement>(`[data-pref-row="${highlight.row}"]`);
      if (!el) {
        if ((tries += 1) < 20) timer = window.setTimeout(find, 100);
        return;
      }
      el.scrollIntoView({ block: 'center', behavior: window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      // as linhas comuns se destacam pelo estado; as de dentro dos blocos (perfil, plano), pela classe
      setFlash(highlight.row);
      el.classList.add('is-flash');
      lit = el;
      timer = window.setTimeout(() => {
        setFlash(null);
        el.classList.remove('is-flash');
        lit = null;
      }, FLASH_MS);
    };
    find();
    return () => {
      window.clearTimeout(timer);
      // outra linha pedida antes de o destaque acabar: esta não fica destacada para sempre
      lit?.classList.remove('is-flash');
    };
  }, [highlight]);

  return (
    <section ref={root} className="rd-prefs__section" aria-labelledby="pref-section-title">
      {before}
      <PageHeader
        className="rd-prefs__head"
        level={2}
        titleId="pref-section-title"
        title={title}
        subtitle={t(section.sub)}
        actions={
          <span className="rd-prefs__scope" title={t(account ? 'prefs.scope.account.tip' : 'prefs.scope.device.tip')}>
            <Icon name={account ? 'cloud' : 'monitor'} size={14} />
            {t(account ? 'prefs.scope.account' : 'prefs.scope.device')}
          </span>
        }
      />
      {(section.top ?? []).map((block) => (
        <Block key={block} id={block} />
      ))}
      {section.cards.map((card) => (
        <CardView key={card.id} card={card} flash={flash} onOpen={onOpen} />
      ))}
      {section.resettable ? <ResetSection id={id} title={title} /> : null}
    </section>
  );
}
